import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form990ShcL = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 990 Schedule L: Transactions with Interested Persons"
          description="Streamline your reporting with Form 990/990EZ Schedule L. Understand and comply with guidelines for transactions involving interested persons effortlessly."
          keywords=""
          canonical="resources/tax-exempt/form-990-schedule-l/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form 990{' '}
          <span className="highlight-container">
            <span className="highlight">Schedule L</span>
          </span>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          E-File Interested Persons Transactions with TaxZerone!
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-990-schedule-l/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 990 Schedule L at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Purpose: Form 990 Schedule L
                        </strong>{' '}
                        is used to report{' '}
                        <strong style={{ fontWeight: 600 }}>
                          transactions between a{' '}
                          <Link
                            href="https://testweb.variniral.com/tax-exempt/"
                            sx={{
                              color: '#0202ff',
                              textDecoration: 'underline',
                            }}
                          >
                            tax-exempt
                          </Link>{' '}
                          organization and interested persons
                        </strong>
                        , helping the IRS identify{' '}
                        <strong style={{ fontWeight: 600 }}>
                          conflicts of interest
                        </strong>{' '}
                        and{' '}
                        <strong style={{ fontWeight: 600 }}>
                          excess benefit transactions
                        </strong>
                        .
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Who Must File?
                        </strong>{' '}
                        Organizations that answer{' '}
                        <strong style={{ fontWeight: 600 }}>"Yes"</strong> to
                        the applicable questions in{' '}
                        <strong style={{ fontWeight: 600 }}>
                          Form 990, Part IV (lines 25a-28c)
                        </strong>{' '}
                        or the corresponding{' '}
                        <Link
                          href="https://www.taxzerone.com/resources/tax-exempt/form-990-ez-instructions/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 990-EZ
                        </Link>{' '}
                        questions generally must complete and attach{' '}
                        <strong style={{ fontWeight: 600 }}>Schedule L</strong>.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Is Reported?
                        </strong>{' '}
                        Schedule L reports{' '}
                        <strong style={{ fontWeight: 600 }}>
                          excess benefit transactions, loans, grants or
                          assistance, and business transactions
                        </strong>{' '}
                        involving interested persons, including{' '}
                        <strong style={{ fontWeight: 600 }}>
                          officers, directors, substantial contributors
                        </strong>
                        , and certain related parties.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Who Is an Interested Person?
                        </strong>{' '}
                        An interested person may include an{' '}
                        <strong style={{ fontWeight: 600 }}>
                          officer, director, trustee, key employee, substantial
                          contributor, family member
                        </strong>
                        , or a related entity, as defined by the IRS.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>

                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Schedule L?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file Schedule L?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#scha" sx={{ color: '#0202ff' }}>
                          Schedule L Filing Requirements
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#choose" sx={{ color: '#0202ff' }}>
                          Choose TaxZerone to complete your Schedule L filing
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Commonly Asked Questions
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="what"
                >
                  What is Schedule L?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong style={{ fontWeight: 600 }}>Schedule L, </strong>
                  <i>Transactions With Interested Persons </i> is a
                  supplementary schedule that accompanies the standard Form 990/
                  <Link
                    href="/resources/tax-exempt/form-990-ez-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    990-EZ
                  </Link>{' '}
                  and is filed annually by tax-exempt and nonprofit
                  organizations.
                </Typography>

                <Typography variant="body1" sx={{ mb: 5 }}>
                  This schedule aims to enhance transparency and accountability
                  within the non-profit sector by providing detailed information
                  on financial dealings between organizations and their
                  affiliated parties.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="who"
                >
                  Who must file Schedule L?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Non-profit organizations that file{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990/"
                    sx={{ color: '#0202ff' }}
                  >
                    IRS Form 990
                  </Link>{' '}
                  or{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-ez/"
                    sx={{ color: '#0202ff' }}
                  >
                    IRS Form 990-EZ
                  </Link>{' '}
                  are generally required to complete Schedule L if they have{' '}
                  <strong style={{ fontWeight: 600 }}>
                    engaged in any transactions or arrangements with interested
                    persons{' '}
                  </strong>
                  during the tax year. This includes transactions such as loans,
                  compensation payments, sales or purchases of assets, and
                  leases.
                </Typography>

                {/* <Typography variant="body1" sx={{ mb: 3 }}>
                  If your organization receives{' '}
                  <strong style={{fontWeight:600}} >
                    substantial contributions meeting or exceeding the $5,000
                  </strong>{' '}
                  threshold from a single donor, Schedule B becomes a necessary
                  filing obligation. You must be attached along with your
                  information return.
                </Typography> */}

                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    mb: 5,
                    border: '1px solid #ccc',
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Choose TaxZerone - Your Gateway to Seamless Tax Filing{' '}
                    <br />
                    Complete your{' '}
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      exempt information return and Schedule L
                    </strong>{' '}
                    filing requirements with ease!
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      textTransform: 'none',
                      boxShadow: 'none',
                      borderRadius: 2,
                    }}
                    size="large"
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990schl`}
                  >
                    E-file Now
                  </Button>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="scha"
                >
                  Schedule L Filing Requirements
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schl-details-img.webp" />
                  <source srcSet="/assets/images/f990-schl-details-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schl-details-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule L showing organization details and basic information"
                    width={760}
                    height={129}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  All Section{' '}
                  <Link
                    href="/resources/tax-exempt/501c3-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    501(c)(3) organizations
                  </Link>{' '}
                  that file Form 990/990-EZ must complete and attach Schedule L.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Below, we have provided Schedule N filing requirements for
                  each part.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part I - Excess Benefit Transactions
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schl-part1-img.webp" />
                  <source srcSet="/assets/images/f990-schl-part1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schl-part1-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule L, Part I, reporting excess benefit transactions involving disqualified persons and related corrections."
                    width={760}
                    height={219}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For each excess benefit transaction with a{' '}
                  <Link
                    href="/resources/tax-exempt/501c3-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    501(c)(3)
                  </Link>
                  , 501(c)(4), or 501(c)(29) organization, provide the
                  following:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (a)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Who got the benefit – List the disqualified person or say
                  “substantial contributor” / “related to substantial
                  contributor” to protect privacy.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (b)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Relationship- Describe how that person is connected to the
                  organization (e.g., officer, family member).{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (c)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Transaction - Explain what the excess benefit transaction
                  was.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (d)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Correction- Say if the issue is fixed.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total excise tax that disqualified persons or
                  organization managers owe for the excess benefit transactions
                  reported on Line 1, even if the IRS hasn’t assessed it yet
                  (unless the tax was waived). This tax is reported and paid
                  using Form 4720.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the amount of that tax that the organization reimbursed
                  to the disqualified persons or managers, if any.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part II - Loans to and/or From Interested Persons
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schl-part2-img.webp" />
                  <source srcSet="/assets/images/f990-schl-part2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schl-part2-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule L, Part II, reporting loans to or from officers, directors, or other interested persons and related terms."
                    width={760}
                    height={283}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report any loans or advances between the organization and
                  “interested persons” (like officers, directors, key employees,
                  or substantial contributors) that are still outstanding at the
                  end of the year. Each loan is reported separately. Certain
                  common transactions (like normal pledges, ordinary employee
                  pay, or loans on standard terms from banks or credit unions)
                  are not reported.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (a){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Name of the interested person involved in the loan.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (b){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {' '}
                  Relationship of that person to the organization.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (c){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Reason or purpose for the loan.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (d){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Shows whether the loan was given by the organization (To) or
                  received by the organization (From).
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (e){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Original loan amount.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (f){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {' '}
                  The amount is still unpaid at the end of the tax year.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (g){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {' '}
                  Whether any payment was overdue or the loan was in
                  default.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (h){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Whether the board or governing body approved the loan.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (i){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Whether there is a signed written loan agreement or promissory
                  note.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part III - Grants or Assistance Benefiting Interested Persons
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schl-part3-img.webp" />
                  <source srcSet="/assets/images/f990-schl-part3-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schl-part3-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule L, Part III, reporting grants or assistance provided to officers, directors, or other interested persons."
                    width={760}
                    height={230}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This part is used to report any grants, help, or benefits the
                  organization gave to an interested person during the tax year.
                  This includes things like scholarships, awards, discounts,
                  internships, prizes, goods, services, or use of facilities no
                  matter the amount.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  (Some items like loans, compensation, or certain charity-wide
                  assistance programs don’t need to be reported here.)
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (a){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Name of the interested person who received the grant or
                  assistance.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (b){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The relationship between that person to the organization.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (c){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Total value or dollar amount of assistance given during the
                  year.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (d){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Type of help provided.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (e){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Reason or purpose for giving assistance.{' '}
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part IV - Business Transactions Involving Interested Persons
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schl-part4-img.webp" />
                  <source srcSet="/assets/images/f990-schl-part4-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schl-part4-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule L, Part IV, reporting business transactions between the organization and officers, directors, or other interested persons."
                    width={760}
                    height={251}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This part is used to report business dealings between the
                  organization and an interested person when the payments meet
                  certain reporting limits. Generally, transactions must be
                  reported if payments are large enough
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (a){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Name of the interested person or entity involved in the
                  business transaction.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (b){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Relationship of that person or entity to the organization
                  (officer, director’s family member, key employee, 35%-owned
                  entity, etc.).
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (c){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Total amount paid or invested by the organization during the
                  year (cash or fair market value).{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (d){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Type of business transaction{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (e){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Check “Yes” if payment was based on a percentage of the
                  organization’s revenue (for example, a fee calculated as a
                  share of income). Otherwise, check “No”.{' '}
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part V - Supplemental Information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schl-part5-img.webp" />
                  <source srcSet="/assets/images/f990-schl-part5-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schl-part5-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule L- Part V Details of Supplemental Information"
                    width={760}
                    height={319}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  This Part is used by organizations to explain or provide
                  additional information regarding a transaction. Schedule L
                  Part V can also be duplicated by the organization in need of
                  additional space to report information.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="choose"
                >
                  Choose TaxZerone to complete your Schedule L filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Embrace the future of tax filing with{' '}
                  <strong style={{ fontWeight: 600 }}>TaxZerone,</strong> your
                  IRS-authorized e-file service provider. At TaxZerone, we
                  believe that e-filing should be simple, not stressful.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  That&apos;s why we&apos;ve designed our platform to be
                  intuitive and user-friendly, guiding you through every step
                  with unwavering support.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Here&apos;s how your Form 990/990-EZ return with Schedule L
                  attachment is transmitted to the IRS - just 3 simple steps!
                </Typography>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Provide Organization Details -
                    </strong>{' '}
                    Choose the tax year for which you want to file a return, and
                    provide your organization&apos;s details.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Preview the Return -
                    </strong>{' '}
                    Complete Schedule L and preview the information provided in
                    the return for accuracy before transmitting.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Transmit to the IRS -
                    </strong>{' '}
                    Transmit Schedule L along with your{' '}
                    <Link
                      href="/resources/tax-exempt/form-990-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      990
                    </Link>
                    /990-EZ return to the IRS and get the acceptance in just a
                    few hours.
                  </li>
                </ol>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  We&apos;ll keep you in the loop with instant updates on your
                  form&apos;s filing status, ensuring a hassle-free experience
                  from start to finish.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  But that&apos;s not all. In the unlikely event that the IRS
                  rejects your exempt return, we&apos;ll help you rectify any
                  errors and resubmit it without any additional cost.
                </Typography>

                {/* <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mb: 5 }}
                  
                >
                  <strong style={{fontWeight:600}} >Note:</strong> Don&apos;t include in Part VI the names of any
                  donors, grantors, or contributors because Part VI will be made
                  available for public inspection.
                </Alert> */}

                <Box
                  component="div"
                  sx={{ mb: 2, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0 }}
                      >
                        Embrace the Future of Tax Filing – Choose TaxZerone
                        Today!
                      </Typography>
                      <Typography variant="body2">
                        Make your e-filing process simple by clicking the button
                        below.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 180, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990schl`}
                      >
                        Start E-filing Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided on this page is based on
                        guidance from the following IRS resources:
                      </Typography>
                      <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                        <li>
                          {' '}
                          <Link
                            href="https://www.irs.gov/pub/irs-pdf/i990sl.pdf"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            Instructions
                          </Link>{' '}
                          for Schedule L (Form 990 or 990-EZ) – Transactions
                          With Interested Persons
                        </li>{' '}
                        <li>
                          {' '}
                          <Link
                            href="https://www.irs.gov/pub/irs-pdf/i990.pdf"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            Instructions
                          </Link>{' '}
                          for Form 990 – Return of Organization Exempt From
                          Income Tax
                        </li>{' '}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ my: 0 }}>
        <Grid
          container
          spacing={2}
          mx={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 0 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
                scrollMarginTop: 83,
              }}
              id="common"
            >
              Commonly Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. What is the purpose of filing Schedule L?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" sx={{ mt: 0.5 }} itemProp="text">
                    Schedule L is used by an organization that files Form 990 or
                    990-EZ to provide information on certain financial
                    transactions or arrangements between the organization and a
                    disqualified person(s) under section 4958 or other
                    interested persons.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. What is an &quot;interested person&quot; in the context of
                  Schedule L?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography sx={{ mt: 1 }}>
                      An interested person is an individual or entity that has a
                      close relationship with the non-profit organization, such
                      as officers, directors, major donors, or business entities
                      controlled by such individuals.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. What types of transactions must be reported on Schedule L?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography sx={{ mt: 0.5, mb: 0 }}>
                      A wide range of transactions with interested persons must
                      be disclosed on Schedule L, including loans, compensation
                      payments, sales or purchases of assets, leases, and any
                      other transactions that exceed certain thresholds.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form990ShcL;
